import { defineColumns } from './piColumns.js';
import { definitionDescription, definitionFor } from './piDefinitions.js';
import { EMPTY, formatDateFull, formatDateTime, formatDuration, formatElapsed, formatInteger, formatPercent, isNumber } from './piFormat.js';
import { MODULE_OPTIONS, optionLabel, roleClassLabels } from './piQuery.js';
import { breakerInfo, heartbeatStale, sourceApiLabel, sourceKindLabel } from './piState.js';

export const STATUS_SECTIONS = [
  { key: 'processamento', label: 'Processamento' },
  { key: 'coleta', label: 'Coleta' },
  { key: 'perdas', label: 'Perdas' },
  { key: 'configuracao', label: 'Configuração' },
  { key: 'categorias', label: 'Categorias' },
  { key: 'eventos', label: 'Eventos' },
  { key: 'metricas', label: 'Métricas' },
];

export const DEFAULT_STATUS_SECTION = 'processamento';

export const statusSection = (value) => {
  const key = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return STATUS_SECTIONS.some((section) => section.key === key) ? key : DEFAULT_STATUS_SECTION;
};

export const JOB_STATUS_LEGEND = [
  { key: 'ok', label: 'OK', tone: 'ok', text: 'A última execução terminou sem problema.' },
  { key: 'done', label: 'Concluído', tone: 'ok', text: 'Etapa de histórico terminada. Só roda de novo com outra solicitação de backfill.' },
  { key: 'clean', label: 'Em dia', tone: 'ok', text: 'Nada esperando recálculo.' },
  { key: 'initialized', label: 'Iniciado', tone: 'info', text: 'Primeira execução no banco: só marcou o ponto de partida. A leitura começa na próxima rodada.' },
  { key: 'running', label: 'Em andamento', tone: 'info', text: 'Rodando agora, ou histórico com mais lotes para as próximas madrugadas.' },
  { key: 'pending', label: 'Na fila', tone: 'info', text: 'Solicitação de backfill aguardando a janela da madrugada (02:30 às 05:30).' },
  { key: 'waiting', label: 'Aguardando janela', tone: 'info', text: 'O backfill só lê os bancos de produção entre 02:30 e 05:30. Fora disso ele confere e espera.' },
  { key: 'idle', label: 'Sem pedidos', tone: 'muted', text: 'Nenhuma solicitação de backfill pendente.' },
  { key: 'aguardando', label: 'Aguardando dados', tone: 'info', text: 'Regra de oportunidade ligada, esperando a janela de dados completar; não gera nada até lá.' },
  { key: 'sem_dado', label: 'Sem dado', tone: 'warn', text: 'Regra de oportunidade sem o dado de que precisa (extração bloqueada, falhou ou ainda não rodou).' },
  { key: 'dirty', label: 'Recálculo pendente', tone: 'warn', text: 'Chegou dado antigo. Os agregados a partir de "Processado até" serão recalculados de madrugada; até lá, o painel marca esses dias como parciais.' },
  { key: 'partial', label: 'Parcial', tone: 'warn', text: 'Rodou, mas parte dos bancos ou dos dias ficou para a próxima execução. O que foi processado já está gravado.' },
  { key: 'blocked_index', label: 'Bloqueado: índice', tone: 'warn', text: 'O banco do cliente não tem o índice que permite ler essa tabela sem varrê-la inteira. A etapa é pulada para não pesar na produção; precisa criar o índice.' },
  { key: 'no_shards', label: 'Sem bancos', tone: 'warn', text: 'Nenhum banco de cliente habilitado para este job.' },
  { key: 'disabled', label: 'Desligado', tone: 'muted', text: 'Job desligado na configuração (chave "jobs" ou "job:<nome>").' },
  { key: 'skipped', label: 'Pulado', tone: 'muted', text: 'A execução foi pulada.' },
  { key: 'failed', label: 'Falhou', tone: 'bad', text: 'Deu erro. Veja "Último erro"; a próxima execução tenta de novo.' },
];

export const PARTIAL_FLAG = {
  label: 'parcial',
  tone: 'warn',
  text: 'Ao lado da situação: o período deste job ainda não foi todo processado, então os números desse trecho ainda podem mudar.',
};

export const SINK_LEGEND = [
  { key: 'closed', label: 'Fechado', tone: 'ok', text: 'Disjuntor normal: a instância grava os eventos no banco de analytics.' },
  { key: 'half_open', label: 'Meio-aberto', tone: 'warn', text: 'Depois de falhas, a instância testa se o banco de analytics voltou.' },
  { key: 'open', label: 'Aberto: descartando', tone: 'bad', text: 'O banco de analytics está falhando e a instância descarta os eventos para não afetar o produto. Os descartes aparecem em "Descartados" e em Perdas.' },
  { key: 'stale', label: 'sem heartbeat recente', tone: 'warn', text: 'Nenhum processo desta máquina reporta há mais de 5 minutos: instância parada ou sink desligado.' },
  { key: 'replaced', label: 'processo substituído', tone: 'muted', text: 'Processo antigo que parou de reportar, mas a mesma máquina já tem outro reportando (reciclagem do IIS). Some sozinho depois de 24 h.' },
  { key: 'config', label: 'Ligado / Desligado', tone: 'muted', text: 'Coluna Config: se a chave do sink desta origem está ligada.' },
  { key: 'invalid', label: 'inválidos', tone: 'bad', text: 'Abaixo de "Descartados": eventos recusados por props malformadas (JSON quebrado, valor que não é texto, número ou booleano, ou com mais de 80 caracteres), empresa ou usuário fora do limite, data fora da janela ou chamada sem requisição. É perda.' },
  { key: 'stripped', label: 'Props podadas', tone: 'warn', text: 'Eventos gravados sem as props que estão fora da allowlist do catálogo do banco. Não é perda: o evento entrou e só a chave desconhecida saiu. O alerta no topo e a dica do número mostram o último evento podado.' },
];

export const LOSS_LEGEND = [
  { key: 'dropped', label: 'Descartados', tone: 'muted', text: 'Eventos perdidos na borda: fila cheia, disjuntor aberto, evento inválido ou falha de gravação. Perda é contada, nunca escondida.' },
  { key: 'partial', label: 'Não (parcial)', tone: 'warn', text: 'Mais de 0,5% dos eventos recebidos no dia foram descartados: os números de uso desse dia ficaram menores que o real.' },
  { key: 'stripped', label: 'Props podadas', tone: 'warn', text: 'Eventos gravados sem as props fora da allowlist do catálogo. Não entra em Descartados porque o evento foi gravado.' },
];

export const CONFIG_LEGEND = [
  { key: 'on', label: 'Ligada', tone: 'ok', text: 'A chave está ativa. As chaves "sink:*" ligam a coleta; "jobs" e "job:*" ligam o processamento.' },
  { key: 'off', label: 'Desligada', tone: 'muted', text: 'A chave está inativa: nada é coletado ou processado por ela.' },
  { key: 'shards', label: 'Bancos habilitados', tone: 'muted', text: 'Quais bancos de cliente a chave cobre. "Todos" quando a chave não restringe bancos.' },
];

const JOB_STATUS_INDEX = JOB_STATUS_LEGEND.reduce((index, entry) => {
  index[entry.key] = entry;
  return index;
}, {});

const STATUS_ALIASES = { success: 'ok', succeeded: 'ok', error: 'failed', halfopen: 'half_open', parcial: 'partial', desligada: 'disabled' };

const normalize = (value) => {
  const key = typeof value === 'string' ? value.trim().toLowerCase().replace(/-/g, '_') : '';
  return STATUS_ALIASES[key] || key;
};

export const jobStatusLegend = (value) => {
  const key = normalize(value);
  if (!key) return { key: '', label: '—', tone: 'muted', text: '' };
  return JOB_STATUS_INDEX[key] || { key: key, label: String(value), tone: 'muted', text: '' };
};

const machineOf = (instance) => {
  const text = typeof instance === 'string' ? instance : '';
  const colon = text.indexOf(':');
  return colon > 0 ? text.slice(0, colon) : text;
};

export const sinkStaleness = (rows, isStale) => {
  const list = Array.isArray(rows) ? rows : [];
  const fresh = {};
  list.forEach((row) => {
    if (!isStale(row)) fresh[(row.sourceApi || '') + '|' + machineOf(row.instance)] = true;
  });
  return list.map((row) => {
    if (!isStale(row)) return '';
    return fresh[(row.sourceApi || '') + '|' + machineOf(row.instance)] ? 'replaced' : 'stale';
  });
};

const PARTIAL_FLAG_STATUSES = ['ok', 'done', 'clean', 'initialized'];

export const showPartialFlag = (status, isPartial) => isPartial === true && PARTIAL_FLAG_STATUSES.indexOf(jobStatusLegend(status).key) !== -1;

export const sinkLegend = (value) => {
  const key = normalize(value);
  return SINK_LEGEND.filter((entry) => entry.key === key)[0] || null;
};

const ATTENTION_TONES = ['warn', 'bad'];

export const jobNeedsAttention = (row) => {
  if (!row) return false;
  const status = jobStatusLegend(row.status);
  return status.key !== 'dirty' && ATTENTION_TONES.indexOf(status.tone) !== -1;
};

const BACKFILL_KINDS = {
  point: 'batidas (db.Point)',
  session: 'sessões de login (db.UserAcess)',
  decision: 'decisões de ajuste (db.AdjustmentReportHistory)',
  request: 'solicitações de ajuste (db.UserPointDayAdjustmentReport)',
  closure: 'fechamentos (db.Closure)',
  recompose: 'recomposição dos dias com histórico',
  windows: 'recálculo das janelas DAU/WAU/MAU',
};

const JOBS = {
  'pa-catalog-sync': 'Sincroniza o catálogo de eventos e métricas',
  'pa-punch-derive': 'Lê as batidas novas dos bancos de clientes',
  'pa-rollup': 'Agrega os eventos por usuário e dia',
  'pa-daily-close': 'Fechamento diário (aplica exclusões tardias e recalcula D-1 e D-2)',
  'pa-snapshot': 'Foto diária das empresas (ativa, plano, assentos)',
  'pa-cohorts': 'Coortes de ativação e retenção',
  'pa-insights': 'Gera os alertas e insights',
  'pa-purge': 'Apaga dados fora da retenção',
  'pa-backfill': 'Executa as solicitações de histórico (backfill)',
  'pa-opportunities': 'Gera as oportunidades de Revenue',
};

const INTENT_KINDS = {
  blocked_click: 'cliques em tela bloqueada',
  contact_request: 'pedidos de contato',
};

const shortShard = (value) => (value && value.length > 12 ? value.slice(0, 8) + '…' : value || '');

export const jobDescription = (key) => {
  const text = typeof key === 'string' ? key : '';
  if (JOBS[text]) return { title: JOBS[text], scope: '' };
  const parts = text.split(':');
  const head = parts[0];
  if (head === 'backfill' && parts.length >= 3) {
    const kind = parts[parts.length - 1];
    return { title: 'Histórico: ' + (BACKFILL_KINDS[kind] || kind), scope: 'banco ' + shortShard(parts.slice(1, -1).join(':')) };
  }
  if (head === 'backfill' && parts.length === 2) return { title: 'Histórico: ' + (BACKFILL_KINDS[parts[1]] || parts[1]), scope: '' };
  if (text === 'windows:dirty-from') return { title: 'Recálculo das janelas DAU/WAU/MAU pendente a partir de', scope: '' };
  if (text === 'cohorts:dirty-from') return { title: 'Recálculo das coortes pendente a partir de', scope: '' };
  if (head === 'punch-derive') return { title: 'Leitura de batidas: posição no banco', scope: 'banco ' + shortShard(parts.slice(1).join(':')) };
  if (head === 'reconcile') return { title: 'Conferência de batidas excluídas depois', scope: 'banco ' + shortShard(parts.slice(1).join(':')) };
  if (head === 'snapshot') return { title: 'Foto das empresas', scope: 'banco ' + shortShard(parts.slice(1).join(':')) };
  if (head === 'daily-close') return { title: 'Fechamento diário (legado)', scope: 'banco ' + shortShard(parts.slice(1).join(':')) };
  if (head === 'coverage' && parts[1] === 'punch-start' && parts.length >= 3) {
    return { title: 'Início da coleta ao vivo de batidas', scope: 'banco ' + parts.slice(2).join(':') };
  }
  if (head === 'opportunity' && parts.length >= 2) return { title: 'Oportunidades: regra ' + parts.slice(1).join(':'), scope: '' };
  if (head === 'intent' && parts.length >= 3) {
    const kind = parts[parts.length - 1];
    return { title: 'Intenção do cliente: ' + (INTENT_KINDS[kind] || kind), scope: 'banco ' + shortShard(parts.slice(1, -1).join(':')) };
  }
  if (head === 'coverage' && parts.length >= 3) {
    return { title: parts[1] === 'punch' ? 'Início da cobertura de batidas' : 'Início da cobertura de atividade', scope: 'banco ' + parts.slice(2).join(':') };
  }
  if (head === 'discarded') return { title: 'Eventos descartados no processamento', scope: parts.slice(1).join(':') };
  return { title: '', scope: '' };
};

export const JOB_FILTERS = [
  { key: 'attention', label: 'Com atenção' },
  { key: 'all', label: 'Todos' },
];

export const filterJobs = (rows, filter) => {
  const list = Array.isArray(rows) ? rows : [];
  return filter === 'attention' ? list.filter((row) => row.attention) : list;
};

const STRIPPED_NOTE = /props_stripped:([A-Za-z0-9_.-]+)(?::([A-Za-z0-9_]+))?/g;

export const strippedEvents = (lastError) => {
  const found = [];
  const text = typeof lastError === 'string' ? lastError : '';
  text.replace(STRIPPED_NOTE, (match, eventName, prop) => {
    const label = prop ? eventName + ' (' + prop + ')' : eventName;
    if (found.indexOf(label) === -1) found.push(label);
    return match;
  });
  return found;
};

export const strippedDetail = (notes) => {
  const events = [];
  (Array.isArray(notes) ? notes : [notes]).forEach((note) => {
    strippedEvents(note).forEach((label) => {
      if (events.indexOf(label) === -1) events.push(label);
    });
  });
  return events.length ? 'Último evento podado: ' + events.join(', ') : '';
};

const countOf = (value) => {
  const number = Number(value);
  return isFinite(number) && number > 0 ? number : 0;
};

const countText = (count, one, many) => formatInteger(count) + ' ' + (count === 1 ? one : many);

const bySource = (rows, field) => {
  const totals = {};
  const order = [];
  rows.forEach((row) => {
    const label = sourceApiLabel(row.sourceApi);
    if (!Object.prototype.hasOwnProperty.call(totals, label)) {
      totals[label] = 0;
      order.push(label);
    }
    totals[label] += countOf(row[field]);
  });
  return order.map((label) => label + ': ' + formatInteger(totals[label])).join(', ');
};

export const collectionAlerts = (sinks) => {
  const rows = Array.isArray(sinks) ? sinks.filter(Boolean) : [];
  const alerts = [];

  const invalid = rows.filter((row) => countOf(row.droppedInvalid24h) > 0);
  const invalidCount = invalid.reduce((sum, row) => sum + countOf(row.droppedInvalid24h), 0);
  if (invalidCount > 0) {
    alerts.push({
      key: 'invalid',
      tone: 'bad',
      count: invalidCount,
      title: countText(invalidCount, 'evento descartado como inválido', 'eventos descartados como inválidos') + ' nas últimas 24 h (' + bySource(invalid, 'droppedInvalid24h') + ')',
      text: 'É perda. Inválido é prop malformada (JSON quebrado, valor que não é texto, número ou booleano, ou com mais de 80 caracteres), empresa ou usuário fora do limite, data fora da janela ou chamada sem requisição. O evento não chega ao banco e não aparece no "Último erro": confira o emissor publicado por último e corrija.',
      detail: '',
    });
  }

  const stripped = rows.filter((row) => countOf(row.propsStripped24h) > 0);
  const strippedCount = stripped.reduce((sum, row) => sum + countOf(row.propsStripped24h), 0);
  if (strippedCount > 0) {
    alerts.push({
      key: 'stripped',
      tone: 'warn',
      count: strippedCount,
      title: countText(strippedCount, 'evento gravado sem props fora da allowlist', 'eventos gravados sem props fora da allowlist') + ' nas últimas 24 h (' + bySource(stripped, 'propsStripped24h') + ')',
      text: 'O evento entrou; só a chave desconhecida saiu. Em geral o emissor foi publicado antes do pa-catalog-sync: rode o pa-catalog-sync em /jobs (ou o sql/02-seed-catalog.sql) e confira a allowlist do evento.',
      detail: strippedDetail(stripped.map((row) => row.lastStrippedNote)),
    });
  }

  return alerts;
};

const rowsOf = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const textOrDash = (value) => (value === null || value === undefined || value === '' ? EMPTY : String(value));

const shown = (value) => (value === EMPTY ? '' : value);

const numberOrNull = (value) => (isNumber(value) ? value : null);

const legendEntry = (key) => SINK_LEGEND.filter((entry) => entry.key === key)[0];

const invalidText = (value) => {
  const count = countOf(value);
  return count ? formatInteger(count) + (count === 1 ? ' inválido' : ' inválidos') : '';
};

const countCell = (value) => (isNumber(value) ? formatInteger(countOf(value)) : EMPTY);

const strippedTitle = (note) => {
  const detail = strippedDetail(note);
  return legendEntry('stripped').text + (detail ? ' ' + detail + '.' : '');
};

export const watermarkRows = (rows) => rowsOf(rows).map((row, index) => {
  const description = jobDescription(row.jobKey);
  return {
    key: row.jobKey || String(index),
    title: description.title,
    scope: description.scope,
    attention: jobNeedsAttention(row),
    lastValue: row.lastValue === null || row.lastValue === undefined ? '' : String(row.lastValue),
    processedUntil: formatDateTime(row.processedUntilUtc),
    lastRun: formatDateTime(row.lastRunUtc),
    lastSuccess: formatDateTime(row.lastSuccessUtc),
    status: jobStatusLegend(row.status),
    partial: showPartialFlag(row.status, row.isPartial),
    rows: formatInteger(row.rowsAffected),
    duration: formatDuration(row.durationMs),
    lastError: row.lastError || '',
    raw: row,
  };
});

export const sinkRows = (rows) => {
  const list = rowsOf(rows);
  const staleness = sinkStaleness(list, (row) => heartbeatStale(row.minutesSinceHeartbeat));
  return list.map((row, index) => {
    const breaker = breakerInfo(row.breakerState);
    const breakerLegend = sinkLegend(row.breakerState);
    return {
      key: (row.instance || '') + ':' + (row.sourceApi || '') + ':' + index,
      source: sourceApiLabel(row.sourceApi),
      instance: row.instance || EMPTY,
      heartbeat: formatDateTime(row.lastHeartbeatUtc),
      elapsed: formatElapsed(row.minutesSinceHeartbeat),
      staleness: staleness[index] ? legendEntry(staleness[index]) : null,
      accepted: formatInteger(row.accepted24h),
      dropped: formatInteger(row.dropped24h),
      droppedPct: isNumber(row.droppedPct24h) ? formatPercent(row.droppedPct24h, 2) : '',
      invalidCount: countOf(row.droppedInvalid24h),
      invalidText: invalidText(row.droppedInvalid24h),
      strippedCount: countOf(row.propsStripped24h),
      stripped: countCell(row.propsStripped24h),
      strippedTitle: strippedTitle(row.lastStrippedNote),
      breaker: breaker,
      breakerText: breakerLegend ? breakerLegend.text : '',
      trouble: staleness[index] === 'stale' || breaker.tone === 'bad' || breaker.tone === 'warn'
        || countOf(row.droppedInvalid24h) > 0 || countOf(row.propsStripped24h) > 0,
      configEnabled: row.configEnabled === true,
      lastError: row.lastError || '',
      raw: row,
    };
  });
};

export const dailyLossRows = (rows) => rowsOf(rows).map((row, index) => ({
  key: (row.date || '') + ':' + (row.sourceApi || '') + ':' + index,
  date: formatDateFull(row.date),
  source: sourceApiLabel(row.sourceApi),
  accepted: formatInteger(row.accepted),
  dropped: formatInteger(row.dropped),
  invalidText: invalidText(row.droppedInvalid),
  droppedPct: formatPercent(row.droppedPct, 2),
  strippedCount: countOf(row.propsStripped),
  stripped: countCell(row.propsStripped),
  partial: row.partial === true,
  raw: row,
}));

export const configRows = (rows) => rowsOf(rows).map((row, index) => ({
  key: row.key || String(index),
  enabled: row.enabled === true,
  shardsText: row.shardsEnabled ? String(row.shardsEnabled) : (String(row.key || '').indexOf('sink:') === 0 ? 'Todos' : EMPTY),
  updatedAtUtc: row.updatedAtUtc,
}));

export const eventRows = (rows) => rowsOf(rows).map((row) => ({
  name: row.name || EMPTY,
  description: row.description || '',
  collectedWhere: row.collectedWhere || '',
  collectedHow: row.collectedHow || '',
  moduleName: row.moduleName || optionLabel(MODULE_OPTIONS, row.module),
  featureType: isNumber(row.featureType) ? row.featureType : null,
  featureText: isNumber(row.featureType) ? String(row.featureType) : EMPTY,
  operationKey: row.operationKey || '',
  ownerText: Number(row.ownerApi) ? sourceApiLabel(row.ownerApi) : EMPTY,
  sourceText: sourceKindLabel(row.sourceKind),
  stage: textOrDash(row.stage),
  isEnabled: row.isEnabled === true,
  status: row.status || '',
  countsText: [row.countsAsActive ? 'ativo' : '', row.countsAsInUse ? 'em uso' : ''].filter(Boolean).join(' e ') || 'não conta',
  rolesText: roleClassLabels(row.roleClasses).join(', ') || EMPTY,
}));

export const metricRows = (rows) => rowsOf(rows).map((row, index) => ({
  key: (row.metricKey || index) + ':' + (row.version || ''),
  metricKey: row.metricKey || EMPTY,
  name: row.name || row.metricKey || EMPTY,
  description: row.description || definitionDescription(definitionFor(row.metricKey)),
  version: textOrDash(row.version),
  formula: textOrDash(row.formula),
  numerator: textOrDash(row.numerator),
  denominator: textOrDash(row.denominator),
  window: textOrDash(row.window),
  timezoneRule: row.timezoneRule || '',
  validity: 'desde ' + formatDateFull(row.validFrom) + (row.validTo ? ' até ' + formatDateFull(row.validTo) : ' (em vigor)'),
  raw: row,
}));

const rawOf = (row) => (row && row.raw ? row.raw : {});

export const JOB_COLUMNS = defineColumns([
  { key: 'job', label: 'Job', type: 'text', infoKey: 'status.job', value: (row) => row.title || row.key },
  { key: 'jobKey', label: 'Chave do job', type: 'text', infoKey: 'status.job', screen: false, value: (row) => row.key },
  { key: 'scope', label: 'Escopo', type: 'text', infoKey: 'status.job', screen: false },
  { key: 'lastValue', label: 'Marca', type: 'int', infoKey: 'status.lastValue', screen: false, value: (row) => numberOrNull(rawOf(row).lastValue) },
  { key: 'processedUntil', label: 'Processado até', type: 'datetime', infoKey: 'status.processedUntil', value: (row) => rawOf(row).processedUntilUtc },
  { key: 'lastRun', label: 'Última execução', type: 'datetime', infoKey: 'status.jobRun', value: (row) => rawOf(row).lastRunUtc },
  { key: 'lastSuccess', label: 'Último sucesso', type: 'datetime', infoKey: 'status.jobRun', value: (row) => rawOf(row).lastSuccessUtc },
  { key: 'status', label: 'Situação', type: 'text', infoKey: 'status.jobStatus', value: (row) => (row.status ? row.status.label : '') },
  { key: 'partial', label: 'Parcial', type: 'text', infoKey: 'status.jobPartial', screen: false },
  { key: 'rows', label: 'Linhas', type: 'int', infoKey: 'status.jobRows', value: (row) => numberOrNull(rawOf(row).rowsAffected) },
  { key: 'duration', label: 'Duração', type: 'int', infoKey: 'status.jobDuration', export: false, exportedAs: ['durationMs'] },
  { key: 'durationMs', label: 'Duração (ms)', type: 'int', infoKey: 'status.jobDuration', screen: false, value: (row) => numberOrNull(rawOf(row).durationMs) },
  { key: 'lastError', label: 'Último erro', type: 'text', infoKey: 'status.lastError' },
]);

export const SINK_COLUMNS = defineColumns([
  { key: 'source', label: 'Origem', type: 'text', infoKey: 'status.sinkSource' },
  { key: 'instance', label: 'Instância', type: 'text', infoKey: 'status.instance', value: (row) => shown(row.instance) },
  { key: 'heartbeat', label: 'Último heartbeat', type: 'datetime', infoKey: 'status.heartbeat', value: (row) => rawOf(row).lastHeartbeatUtc },
  {
    key: 'heartbeatAge', label: 'Minutos desde o último heartbeat', type: 'int', infoKey: 'status.heartbeat', screen: false,
    value: (row) => numberOrNull(rawOf(row).minutesSinceHeartbeat),
  },
  { key: 'heartbeatSignal', label: 'Sinal do heartbeat', type: 'text', infoKey: 'status.heartbeat', screen: false, value: (row) => (row.staleness ? row.staleness.label : '') },
  { key: 'accepted', label: 'Aceitos', type: 'int', infoKey: 'status.accepted', value: (row) => numberOrNull(rawOf(row).accepted24h) },
  { key: 'dropped', label: 'Descartados', type: 'int', infoKey: 'status.dropped', value: (row) => numberOrNull(rawOf(row).dropped24h) },
  { key: 'droppedShare', label: '% descartado', type: 'percent', infoKey: 'status.droppedShare', screen: false, value: (row) => numberOrNull(rawOf(row).droppedPct24h) },
  { key: 'invalid', label: 'Inválidos', type: 'int', infoKey: 'status.invalid', screen: false, value: (row) => numberOrNull(rawOf(row).droppedInvalid24h) },
  { key: 'stripped', label: 'Props podadas', type: 'int', infoKey: 'status.stripped', value: (row) => numberOrNull(rawOf(row).propsStripped24h) },
  { key: 'lastStripped', label: 'Último evento podado', type: 'text', infoKey: 'status.stripped', screen: false, value: (row) => strippedEvents(rawOf(row).lastStrippedNote) },
  { key: 'breaker', label: 'Disjuntor', type: 'text', infoKey: 'status.breaker', value: (row) => shown(row.breaker ? row.breaker.label : '') },
  { key: 'config', label: 'Config', type: 'text', infoKey: 'status.sinkConfig', value: (row) => (row.configEnabled ? 'Ligado' : 'Desligado') },
  { key: 'lastError', label: 'Último erro', type: 'text', infoKey: 'status.lastError' },
]);

export const LOSS_COLUMNS = defineColumns([
  { key: 'day', label: 'Dia', type: 'date', infoKey: 'status.lossDay', value: (row) => rawOf(row).date },
  { key: 'source', label: 'Origem', type: 'text', infoKey: 'status.sinkSource' },
  { key: 'accepted', label: 'Aceitos', type: 'int', infoKey: 'status.accepted', value: (row) => numberOrNull(rawOf(row).accepted) },
  { key: 'dropped', label: 'Descartados', type: 'int', infoKey: 'status.dropped', value: (row) => numberOrNull(rawOf(row).dropped) },
  { key: 'invalid', label: 'Inválidos', type: 'int', infoKey: 'status.invalid', screen: false, value: (row) => numberOrNull(rawOf(row).droppedInvalid) },
  { key: 'droppedPct', label: '% descartado', type: 'percent', infoKey: 'status.droppedShare', value: (row) => numberOrNull(rawOf(row).droppedPct) },
  { key: 'stripped', label: 'Props podadas', type: 'int', infoKey: 'status.stripped', value: (row) => numberOrNull(rawOf(row).propsStripped) },
  { key: 'complete', label: 'Dia completo', type: 'text', infoKey: 'status.dayComplete', value: (row) => (row.partial ? 'Não (parcial)' : 'Sim') },
]);

export const CONFIG_COLUMNS = defineColumns([
  { key: 'key', label: 'Chave', type: 'text', infoKey: 'status.configKey' },
  { key: 'enabled', label: 'Situação', type: 'text', infoKey: 'status.configEnabled', value: (row) => (row.enabled ? 'Ligada' : 'Desligada') },
  { key: 'shards', label: 'Bancos habilitados', type: 'text', infoKey: 'status.configShards', value: (row) => shown(row.shardsText) },
  { key: 'updated', label: 'Atualizada em', type: 'datetime', infoKey: 'status.configUpdated', value: (row) => row.updatedAtUtc },
]);

export const EVENT_COLUMNS = defineColumns([
  { key: 'name', label: 'Evento', type: 'text', infoKey: 'status.eventName', sortable: true, startDescending: false, value: (row) => shown(row.name) },
  { key: 'description', label: 'Descrição', type: 'text', infoKey: 'status.eventName', screen: false },
  { key: 'collectedWhere', label: 'Onde é coletado', type: 'text', infoKey: 'status.eventCollection', screen: false },
  { key: 'collectedHow', label: 'Como é coletado', type: 'text', infoKey: 'status.eventCollection', screen: false },
  { key: 'operationKey', label: 'Chave da operação', type: 'text', infoKey: 'status.eventOperation', screen: false },
  { key: 'moduleName', label: 'Módulo', type: 'text', infoKey: 'status.eventModule', sortable: true, startDescending: false, value: (row) => shown(row.moduleName) },
  { key: 'featureType', label: 'Funcionalidade', type: 'int', infoKey: 'status.eventFeature', sortable: true, startDescending: false },
  { key: 'ownerText', label: 'Origem', type: 'text', infoKey: 'status.eventOwner', sortable: true, startDescending: false, value: (row) => shown(row.ownerText) },
  { key: 'sourceText', label: 'Tipo de fonte', type: 'text', infoKey: 'status.eventOwner', screen: false, value: (row) => shown(row.sourceText) },
  { key: 'stage', label: 'Etapa', type: 'text', infoKey: 'status.eventStage', sortable: true, startDescending: false, value: (row) => shown(row.stage) },
  {
    key: 'isEnabled', label: 'Situação', type: 'text', infoKey: 'status.eventEnabled', sortable: true, startDescending: true,
    value: (row) => (row.isEnabled ? 'Habilitado' : 'Desligado'),
  },
  { key: 'status', label: 'Situação no catálogo', type: 'text', infoKey: 'status.eventEnabled', screen: false },
  { key: 'countsText', label: 'Conta como', type: 'text', infoKey: 'status.eventCounts' },
  { key: 'rolesText', label: 'Perfis', type: 'text', infoKey: 'status.eventRoles', value: (row) => shown(row.rolesText) },
]);

export const METRIC_COLUMNS = defineColumns([
  { key: 'name', label: 'Métrica', type: 'text', infoKey: 'status.metricName', value: (row) => shown(row.name) },
  { key: 'metricKey', label: 'Chave da métrica', type: 'text', infoKey: 'status.metricName', screen: false, value: (row) => shown(row.metricKey) },
  { key: 'description', label: 'Descrição', type: 'text', infoKey: 'status.metricName', screen: false },
  { key: 'version', label: 'Versão', type: 'int', infoKey: 'status.metricVersion', value: (row) => numberOrNull(rawOf(row).version) },
  { key: 'formula', label: 'Fórmula', type: 'text', infoKey: 'status.metricFormula', value: (row) => rawOf(row).formula || '' },
  { key: 'numerator', label: 'Numerador', type: 'text', infoKey: 'status.metricFormula', value: (row) => rawOf(row).numerator || '' },
  { key: 'denominator', label: 'Denominador', type: 'text', infoKey: 'status.metricFormula', value: (row) => rawOf(row).denominator || '' },
  { key: 'window', label: 'Janela', type: 'text', infoKey: 'status.metricWindow', value: (row) => rawOf(row).window || '' },
  { key: 'timezoneRule', label: 'Fuso', type: 'text', infoKey: 'status.metricWindow', screen: false },
  { key: 'validity', label: 'Vigência', type: 'text', infoKey: 'status.metricValidity', export: false, exportedAs: ['validFrom', 'validTo'] },
  { key: 'validFrom', label: 'Vigente desde', type: 'date', infoKey: 'status.metricValidity', screen: false, value: (row) => rawOf(row).validFrom || null },
  { key: 'validTo', label: 'Vigente até', type: 'date', infoKey: 'status.metricValidity', screen: false, value: (row) => rawOf(row).validTo || null },
]);



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piStatusModel.js